import type { HabitacionesResponse } from '../lib/api.js';
import { fmtBs, fmtInt, fmtPct, fmtUsd } from '../lib/format.js';
import { ChartCard } from './charts/ChartCard.js';
import { RoomTypeBars } from './charts/RoomTypeBars.js';

/**
 * Panel de TIPOS DE HABITACIÓN: qué tipo se vende, a qué ocupación y a qué
 * tarifa (reporte "Statistics - Room Type").
 *
 * Va por RANGO —no anual como canales o cuentas— porque este reporte sí baja a
 * día y puede seguir el filtro del dashboard.
 *
 * La **cobertura** se rotula siempre: el reporte llega un PDF por tipo, así que
 * lo normal es tener cargados solo algunos, y leer su total como si fuera el
 * hotel es justo el error que hay que impedir.
 */
export function HabitacionesPanel({ data }: { data: HabitacionesResponse }) {
  if (data.sinDatos) {
    return (
      <ChartCard title="Tipos de habitación">
        <p className="text-sm text-slate-500">
          Sin datos por tipo de habitación en el rango. Se alimentan del reporte «Statistics - Room
          Type» (un PDF por tipo), que se sube en Ingesta.
        </p>
      </ChartCard>
    );
  }

  const parcial = data.coberturaHotel != null && data.coberturaHotel < 0.995;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
          Tipos de habitación
        </h2>
        <span className="text-xs text-slate-500">
          {data.tipos.length} tipo(s) · {data.dias} día(s) con dato
        </span>
      </div>

      {/* La cobertura va ARRIBA del dato: si estos tipos son media plantilla, hay
          que saberlo antes de leer las cifras, no después. */}
      {parcial && (
        <div className="rounded-lg border border-amber-700/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
          Estos {data.tipos.length} tipo(s) suman {fmtInt(data.noches)} de las{' '}
          {fmtInt(data.nochesHotel ?? 0)} noches ocupadas del hotel en el rango (
          {fmtPct((data.coberturaHotel ?? 0) * 100)}): faltan tipos por cargar. Las cifras de abajo
          son de lo reportado, no del hotel completo.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Noches vendidas por tipo">
          <RoomTypeBars rows={data.tipos} />
        </ChartCard>

        <ChartCard title="Detalle por tipo">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-2 pr-3 text-left font-medium">Tipo</th>
                  <th className="px-2 py-2 text-right font-medium">Noches</th>
                  <th className="px-2 py-2 text-right font-medium">% ocup.</th>
                  <th className="px-2 py-2 text-right font-medium">ADR</th>
                  <th className="px-2 py-2 text-right font-medium">Pers./noche</th>
                </tr>
              </thead>
              <tbody>
                {data.tipos.map((t) => (
                  <tr key={t.roomType} className="border-b border-slate-800/80">
                    <td className="py-1.5 pr-3">
                      <span className="font-medium text-slate-200">{t.roomType}</span>
                      {t.descripcion && (
                        <span className="ml-2 text-[11px] text-slate-500">{t.descripcion}</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">
                      {fmtInt(t.noches)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">
                      {t.occPct == null ? '—' : fmtPct(t.occPct * 100)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">
                      {t.adrBs == null ? '—' : fmtBs(t.adrBs)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">
                      {t.personasPorNoche == null ? '—' : t.personasPorNoche.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-700 text-slate-100">
                  <td className="py-2 pr-3 text-xs uppercase tracking-wide text-slate-400">
                    Reportado
                  </td>
                  <td className="px-2 py-2 text-right font-medium tabular-nums">
                    {fmtInt(data.noches)}
                  </td>
                  <td className="px-2 py-2 text-right font-medium tabular-nums">
                    {data.occPct == null ? '—' : fmtPct(data.occPct * 100)}
                  </td>
                  <td className="px-2 py-2 text-right font-medium tabular-nums">
                    {data.adrBs == null ? '—' : fmtBs(data.adrBs)}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-slate-400">
                    {data.adrUsd == null ? '' : fmtUsd(data.adrUsd)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            El <strong>% de ocupación es de cada tipo contra su propio inventario</strong>, no
            contra el del hotel: es lo único comparable entre tipos de tamaños muy distintos.
          </p>
        </ChartCard>
      </div>
    </div>
  );
}
